import React, { useMemo } from 'react';
import classNames from 'classnames';
import { IconChevronDown, IconDirUp } from 'components/Icons';
import { Dropdown } from 'presentation/components/KSQ2/Common/Dropdown/Dropdown';
import { Tooltip, Whisper } from 'rsuite';
import KAILoader from 'presentation/components/KAI/KAILoader';
import { DestinationType } from '../../hooks';

export interface SendButtonProps {
  // Destination state
  selectedDestination: DestinationType;
  onDestinationSelect: (destination: DestinationType) => void;
  destinationDropdownOpen: boolean;
  onDropdownOpenChange: (open: boolean) => void;

  // Visibility flags
  canManageQueue: boolean;
  hasDropdownOptions: boolean;
  shouldShowInternal: boolean;
  shouldShowEmailOption: boolean;
  isClosedAndReopen: boolean;
  hideReopenLabel: boolean;
  isCreateMode?: boolean;

  // State flags
  isValidText: boolean;
  isPending: boolean;
  messageLoading: boolean;

  // Styling
  composerModeClass: string;

  // Handlers
  onSubmit: () => void;

  // Custom label
  submitLabel?: string;
}

/**
 * Send button component with destination dropdown for the CMComposer
 */
export const SendButton: React.FC<SendButtonProps> = ({
  selectedDestination,
  onDestinationSelect,
  destinationDropdownOpen,
  onDropdownOpenChange,
  canManageQueue,
  hasDropdownOptions,
  shouldShowInternal,
  shouldShowEmailOption,
  isClosedAndReopen,
  hideReopenLabel,
  isValidText,
  isPending,
  messageLoading,
  composerModeClass,
  isCreateMode = false,
  onSubmit,
  submitLabel,
}) => {
  const actionLabel = useMemo(() => {
    if (isClosedAndReopen && !hideReopenLabel) return 'REOPEN';
    if (submitLabel) return submitLabel;
    if (isCreateMode) return 'CREATE';
    return 'SEND';
  }, [isClosedAndReopen, hideReopenLabel, isCreateMode, submitLabel]);

  const getTooltipText = () => {
    if (isClosedAndReopen && !hideReopenLabel) return 'Reopen request';
    if (selectedDestination === 'INTERNAL') return 'Send as internal comment';
    if (selectedDestination === 'EMAIL') return 'Send as email';
    return 'Send your request';
  };

  const getButtonLabel = () => {
    if (isClosedAndReopen && !hideReopenLabel) return 'REOPEN';
    // We need to map the values to certain strings
    if (selectedDestination === 'ALL') return canManageQueue ? 'Send to Client' : 'Send to Accounting';
    if (selectedDestination === 'INTERNAL') return 'internal team';
    if (selectedDestination === 'EMAIL') return 'email';
    return selectedDestination;
  };

  return (
    <div className="right-actions__submit">
      <div className="expanded-send-container">
        <div className={classNames('unified-send-button expanded', composerModeClass)}>
          {canManageQueue && hasDropdownOptions ? (
            <>
              <Dropdown
                trigger={
                  <div className="destination-selector-expanded">
                    <span className="destination-label">{getButtonLabel()}</span>
                    <span className="dropdown-arrow">
                      <IconChevronDown />
                    </span>
                  </div>
                }
                position="top"
                alignment="end"
                offset={{ x: 0, y: -10 }}
                className="destination-dropdown-wrapper"
                menuClassName="destination-dropdown"
                minWidth={260}
                isOpen={destinationDropdownOpen}
                onOpenChange={onDropdownOpenChange}
              >
                <div className="destination-dropdown__header">Where to send this message?</div>
                <div
                  className={classNames('destination-dropdown__item', {
                    active: selectedDestination === 'ALL',
                  })}
                  onClick={() => onDestinationSelect('ALL')}
                >
                  <span className="destination-dropdown__dot destination-dropdown__dot--grey" />
                  {canManageQueue ? 'Send to Client (Visible to All)' : 'Send to Accounting'}
                </div>
                {shouldShowInternal && (
                  <div
                    className={classNames('destination-dropdown__item', {
                      active: selectedDestination === 'INTERNAL',
                    })}
                    onClick={() => onDestinationSelect('INTERNAL')}
                  >
                    <span className="destination-dropdown__dot destination-dropdown__dot--yellow" />
                    Send to internal team only
                  </div>
                )}
                {shouldShowEmailOption && (
                  <div
                    className={classNames('destination-dropdown__item', {
                      active: selectedDestination === 'EMAIL',
                    })}
                    onClick={() => onDestinationSelect('EMAIL')}
                  >
                    <span className="destination-dropdown__dot destination-dropdown__dot--green" />
                    Send externally (email)
                  </div>
                )}
              </Dropdown>
              <div className="button-divider" />
            </>
          ) : canManageQueue ? (
            <>
              <span className="destination-label" style={{ padding: '0 12px' }}>
                {actionLabel}
              </span>
              <div className="button-divider" />
            </>
          ) : (
            <span className="single-destination">{actionLabel}</span>
          )}

          <Whisper placement="top" trigger="hover" delayOpen={1200} speaker={<Tooltip>{getTooltipText()}</Tooltip>}>
            <button
              className={classNames('send-button-expanded', {
                disabled: !isValidText,
                reopen: isClosedAndReopen && isValidText,
                single: !canManageQueue,
              })}
              onClick={onSubmit}
              disabled={isPending || messageLoading}
            >
              {messageLoading ? <KAILoader small={true} /> : <IconDirUp />}
            </button>
          </Whisper>
        </div>
      </div>
    </div>
  );
};

export default SendButton;
